// src/pages/system/PermissionForm.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Typography
} from '@mui/material';
import {
    Security as SecurityIcon
} from '@mui/icons-material';

const PermissionForm = ({ open, onClose, onSubmit, permission = null }) => {
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        module: ''
    });
    const [modules, setModules] = useState([]);
    const [error, setError] = useState('');
    const [newModule, setNewModule] = useState('');
    const [showNewModuleInput, setShowNewModuleInput] = useState(false);

    // Módulos predefinidos comunes
    const commonModules = [
        'clients',
        'finance',
        'inventory',
        'system',
        'reports',
        'support',
        'network'
    ];

    useEffect(() => {
        if (open) {
            fetchModules();
            if (permission) {
                setFormData({
                    name: permission.name || '',
                    code: permission.code || '',
                    description: permission.description || '',
                    module: permission.module || ''
                });
            } else {
                setFormData({
                    name: '',
                    code: '',
                    description: '',
                    module: ''
                });
            }
            setShowNewModuleInput(false);
            setNewModule('');
        }
    }, [open, permission]);

    const fetchModules = async () => {
        try {
            const response = await fetch('/api/roles/modules', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Combinar módulos existentes con los comunes
                const allModules = [...new Set([...commonModules, ...data])];
                setModules(allModules);
            }
        } catch (error) {
            console.error('Error fetching modules:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Auto-generar código basado en el nombre
        if (name === 'name' && !permission) {
            const autoCode = value.toLowerCase()
                .replace(/[^a-z0-9\s]/g, '')
                .replace(/\s+/g, '.')
                .substring(0, 50);

            setFormData(prev => ({
                ...prev,
                code: autoCode
            }));
        }
    };

    const handleModuleChange = (e) => {
        const value = e.target.value;
        if (value === 'new') {
            setShowNewModuleInput(true);
        } else {
            setFormData(prev => ({
                ...prev,
                module: value
            }));
            setShowNewModuleInput(false);
        }
    };

    const handleNewModuleConfirm = () => {
        if (newModule.trim()) {
            setFormData(prev => ({
                ...prev,
                module: newModule.trim()
            }));
            setShowNewModuleInput(false);
            setNewModule('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Validaciones
        if (!formData.name.trim()) {
            setError('El nombre del permiso es obligatorio');
            return;
        }

        if (!formData.code.trim()) {
            setError('El código del permiso es obligatorio');
            return;
        }

        if (!formData.module.trim()) {
            setError('El módulo es obligatorio');
            return;
        }

        // Validar formato del código
        const codeRegex = /^[a-z0-9._-]+$/;
        if (!codeRegex.test(formData.code)) {
            setError('El código solo puede contener letras minúsculas, números, puntos, guiones y guiones bajos');
            return;
        }

        onSubmit(formData);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {permission ? 'Editar Permiso' : 'Nuevo Permiso'}
                </DialogTitle>
                <DialogContent>
                    <Box className="space-y-4 py-4">
                        {error && (
                            <Alert severity="error" onClose={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label="Nombre del Permiso"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Ej: Ver Clientes, Crear Facturas"
                            helperText="Nombre descriptivo del permiso"
                        />

                        <TextField
                            fullWidth
                            label="Código del Permiso"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            placeholder="Ej: clients.view, invoices.create"
                            helperText="Código único del permiso (auto-generado desde el nombre)"
                        />

                        <TextField
                            fullWidth
                            label="Descripción"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            placeholder="Descripción detallada del permiso"
                            helperText="Explica qué permite hacer este permiso"
                        />

                        {!showNewModuleInput ? (
                            <FormControl fullWidth required>
                                <InputLabel>Módulo</InputLabel>
                                <Select
                                    name="module"
                                    value={formData.module}
                                    onChange={handleModuleChange}
                                    label="Módulo"
                                >
                                    {modules.map(module => (
                                        <MenuItem key={module} value={module}>
                                            <Box className="flex items-center justify-between w-full">
                                                <span>{module}</span>
                                                <Chip size="small" label={module} color="primary" variant="outlined" />
                                            </Box>
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="new">
                                        <em>+ Crear nuevo módulo</em>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        ) : (
                            <Box className="flex gap-2">
                                <TextField
                                    fullWidth
                                    label="Nuevo Módulo"
                                    value={newModule}
                                    onChange={(e) => setNewModule(e.target.value)}
                                    placeholder="Nombre del nuevo módulo"
                                    autoFocus
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleNewModuleConfirm}
                                    disabled={!newModule.trim()}
                                >
                                    Confirmar
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        setShowNewModuleInput(false);
                                        setNewModule('');
                                    }}
                                >
                                    Cancelar
                                </Button>
                            </Box>
                        )}

                        {formData.module && (
                            <Box className="mt-2">
                                <Typography variant="caption" color="textSecondary">
                                    Módulo seleccionado:
                                </Typography>
                                <Chip
                                    size="small"
                                    label={formData.module}
                                    color="primary"
                                    variant="outlined"
                                    className="ml-2"
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={<SecurityIcon />}
                    >
                        {permission ? 'Actualizar Permiso' : 'Crear Permiso'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default PermissionForm;